import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { FAMILY_SIZES } from '@twinden/shared';
import {
  ReadingColumn,
  SiteShell,
  richText,
} from '../components/site/SiteChrome';
import { api, ApiError } from '../lib/api';
import { useT, type MessageKey } from '../i18n';

// Asking for an invitation (M44, docs/invitation-requests.md D114–D115).

const SIZE_LABELS: Record<(typeof FAMILY_SIZES)[number], MessageKey> = {
  '2-5': 'site.requestInvite.size2to5',
  '6-10': 'site.requestInvite.size6to10',
  '11-20': 'site.requestInvite.size11to20',
  '20+': 'site.requestInvite.size20plus',
};

export function RequestInvitePage() {
  const t = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [familySize, setFamilySize] = useState<string>('2-5');
  // People never see this field; bots fill it in (D115).
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.requestInvite({
        name: name.trim(),
        email: email.trim(),
        familySize,
        ...(website ? { website } : {}),
      });
      setSentTo(email.trim());
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t('common.somethingWrong')
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <SiteShell>
      <ReadingColumn>
        <VStack align="stretch" spacing={6}>
          <VStack align="stretch" spacing={3}>
            <Heading
              as="h1"
              fontSize={{ base: '36px', md: '48px' }}
              lineHeight={1.05}
            >
              {t('site.requestInvite.title')}
            </Heading>
            <Text fontSize="18px" lineHeight="27px" color="ink.body">
              {t('site.requestInvite.intro')}
            </Text>
          </VStack>

          {sentTo ? (
            <Box bg="accent.tint" borderRadius="card" p={6}>
              <VStack align="flex-start" spacing={3}>
                <Box color="accent">
                  <Check size={28} />
                </Box>
                <Heading as="h2" fontSize="24px">
                  {t('site.requestInvite.thanks')}
                </Heading>
                <Text color="ink.body">
                  {richText(t('site.requestInvite.sent'), {
                    email: <strong>{sentTo}</strong>,
                    link: (
                      <Link
                        as={RouterLink}
                        to="/"
                        color="accent"
                        fontWeight={600}
                      >
                        {t('site.requestInvite.moreLink')}
                      </Link>
                    ),
                  })}
                </Text>
              </VStack>
            </Box>
          ) : (
            <Box
              as="form"
              onSubmit={submit}
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              p={{ base: 5, md: 6 }}
            >
              <VStack align="stretch" spacing={5}>
                <FormControl isRequired>
                  <FormLabel>{t('site.requestInvite.name')}</FormLabel>
                  <Input
                    h="48px"
                    value={name}
                    maxLength={100}
                    autoComplete="name"
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>{t('site.requestInvite.email')}</FormLabel>
                  <Input
                    h="48px"
                    type="email"
                    value={email}
                    maxLength={254}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <FormHelperText>
                    {t('site.requestInvite.emailHint')}
                  </FormHelperText>
                </FormControl>
                <FormControl as="fieldset">
                  <FormLabel as="legend">
                    {t('site.requestInvite.size')}
                  </FormLabel>
                  <RadioGroup value={familySize} onChange={setFamilySize}>
                    <Stack
                      direction={{ base: 'column', sm: 'row' }}
                      spacing={{ base: 2, sm: 5 }}
                    >
                      {FAMILY_SIZES.map((size) => (
                        <Radio key={size} value={size} colorScheme="brand">
                          {t(SIZE_LABELS[size])}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                </FormControl>
                {/* Hidden from people (and screen readers); bots fill it in. */}
                <Box position="absolute" left="-10000px" aria-hidden="true">
                  <label>
                    Website
                    <input
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      name="website"
                    />
                  </label>
                </Box>
                {error && (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                <Button
                  type="submit"
                  h="52px"
                  isLoading={sending}
                  loadingText={t('site.requestInvite.sending')}
                >
                  {t('site.requestInvite.title')}
                </Button>
              </VStack>
            </Box>
          )}
          <Text color="muted" fontSize="15px">
            {richText(t('site.requestInvite.privacy'), {
              link: (
                <Link
                  as={RouterLink}
                  to="/privacy"
                  color="accent"
                  fontWeight={600}
                >
                  {t('site.link.privacyPolicy')}
                </Link>
              ),
            })}
          </Text>
        </VStack>
      </ReadingColumn>
    </SiteShell>
  );
}
